// Angular
import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';

// SyncFusion
import { Column, ExcelExportProperties, ExportHelper, getForeignData, GridComponent } from '@syncfusion/ej2-angular-grids';
import { MenuItem } from '@syncfusion/ej2-angular-navigations';
import { getValue } from '@syncfusion/ej2-base';

// Data
import { CerDataService } from '@data/cer-data.service';

// Ui
import { UiCommandEvent } from '@ui/cer-ui-types';
import { uiCmdExportExcel, uiCmdImportExcel } from '@ui/cer-ui-commands';

// Grid
import { CerGridCommandService } from './cer-grid-command.service';
import { FieldMetadata, UiCommandRenderCell, ViewMetadata } from '@data/cer-data-types';
import { CerGridEditService } from './cer-grid-edit.service';
import { CerDataImportDialogService } from '@data/cer-data-excel-import/cer-data-import-dialog.service';
import { CerDataImportClipboardService } from '@data/cer-data-excel-import/cer-data-import-clipboard.service';
import { CerDataImportExcelReader, CerExcelWorkbook } from '@data/cer-data-excel-import/cer-data-import-excel-reader';
import { CerClipboardPasteFileReader } from '@data/cer-data-excel-import/cer-clipboard-paste-reader';
import { CerGridComponent } from './cer-grid.component';
import { CerGridFilterService } from './cer-grid-filter.service';
import { CerPromptService } from '@cer/cer-ui/cer-ui-prompt.service';

@Injectable()
export class CerGridExcelService implements OnDestroy {
  // Subscriptions
  private subscriptionManager: Subscription = new Subscription();

  // Grid edit
  //public appGrid: CerGridComponent;
  public grid: GridComponent;

  // Constructor
  constructor(private data: CerDataService,
    private command: CerGridCommandService,
    private edit: CerGridEditService,
    private datePipe: DatePipe,
    private ui: CerPromptService,
    private clipboardService: CerDataImportClipboardService,
    private importDialogService: CerDataImportDialogService) {
    this.subscriptionManager.add(command.commandPre$.subscribe(cmd => this.onCommandPre(cmd)));
  }

  // Subscriptions
  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }

  public init(appGrid: CerGridComponent) {
    //this.appGrid = appGrid;
    this.grid = appGrid.grid;
    //this.importService.init(appGrid);
  }

  public onCommandPre(event: UiCommandEvent) {
    switch (event.source) {
      case 'actionBegin':
        switch (event.actionType) {
          case 'excelExportHeaderCell':
            this.onExcelQueryHeaderCell(event.args);
            break;
          case 'excelExportCell':
            this.onExcelExportCell(event.args);
            break;
          case 'excelExportFinalPageSetup':
            this.onExcelFinalPageSetup(event.args);
            this.doExportExcelPageSetup(event.args);
            break;
        }
        break;
      case 'contextMenu':
        switch (event.actionType) {
          case 'contextMenuOpen':
            this.onContextMenuOpen(event);
            break;
        }
        break;
    }
    switch (event.commandId) {
      case uiCmdExportExcel.id:
        this.doExportExcel();
        break;
      case uiCmdImportExcel.id:
        this.doImportExcel(event.appComponent as CerGridComponent, uiCmdImportExcel.tooltipText);
        break;
    }
  }

  // Clipboard
  private onContextMenuOpen(event: UiCommandEvent) {
    const menuItems: MenuItem[] = event.args.items as MenuItem[];
    const excelImport = menuItems.find(i => i.id == uiCmdImportExcel.id);
    if (excelImport) {
      const isVisible = this.command.contextMenuItemIsActive(excelImport, event.args.rowInfo.target);
      if (isVisible) {
        const excelImportVisible = this.edit.canAdd() && this.edit.canEditStart();
        this.command.contextMenuVisible(excelImport, excelImportVisible);
      }
    }
  }

  // Excel import
  public async doImportExcel(appGrid: CerGridComponent, title: string) {

    this.clipboardService.onFilesCallback = (fileList: FileList) => {
      if (fileList.length > 0) {
        this.importDialogService.dialogHide();
      }
      this.readExcelFiles(fileList, appGrid);
    };

    this.clipboardService.wireEvent();
    const dropEle = this.clipboardService.dropElementGet(['/assets/icons/excel.svg'], ".xlsx,.xls");
    const action = await this.importDialogService.dialogOpen(dropEle, title);
    if (action == 'reload') {
      //this.clipboardService.readClipboardItems(CerDataImportExcelReader.legalMimeTypes);
    }
    this.clipboardService.unwireEvent()
  }

  private readExcelFiles(fileList: FileList, appGrid: CerGridComponent) {
    CerClipboardPasteFileReader.readFromClipboard(
      fileList, new CerDataImportExcelReader(), CerDataImportExcelReader.legalMimeTypes, this.ui)
      .then(workbooks => {
        this.ui.info("Filer hentet");
        this.importDialogService.importFile(workbooks as CerExcelWorkbook[], appGrid);
      });
  }

  // Excel export
  private moduleWorkbook(): any {
    return (<any>this.grid?.excelExportModule)?.book;
  }

  private onExcelFinalPageSetup(workbook: any) {
    if (workbook) {
      const sheets: any[] = workbook.worksheets;
      sheets?.forEach((s, idx) => {
        const suffix = idx == 0 ? "" : "-" + (idx + 1);
        s.name = `Data${suffix}`;
        this.fitColumnsWidth(s);
      });
      this.doExportExcelProperties(workbook);
    }
  }

  private fitColumnsWidth(sheet: any) {
    var cIdx = 0;
    (sheet.columns as any[]).forEach(c => {
      if (c.width > 0) {
        var rIdx = 0;
        (sheet.rows as any[]).forEach(r => {
          if (rIdx > 0) {
            const value: any = r.cells[cIdx];
            if (value != null) {
              const s = value.toString();
              const l = s?.length;
              if (l > 4) {
                const cWidth: number = l * 6;
                if (cWidth > (c.width as number)) {
                  c.width = cWidth;
                }
              }
            }
          }
          rIdx++;
        });
        cIdx++;
      }
    });
  }

  private tags: string;
  private doExportExcelProperties(workbook: any) {
    if (workbook != null && workbook.builtInProperties == null && this.tags?.length > 0) {
      workbook.builtInProperties = {};
      workbook.builtInProperties.tags = this.tags;
    }
  }

  public onExcelQueryHeaderCell(args: any) {
    //args.hyperLink = { target: field };
    args.style ??= {};
    args.style.hAlign = 'left';
    args.style.bold = false;
    args.style.font ??= {}
    args.style.font.color = '#00000';
    args.style.border ??= {};
    args.style.border.bottom ??= {};
    args.style.border.bottom
  }

  public async onExcelExportCell(e: UiCommandRenderCell): Promise<any> {
    e.fieldMeta?.onExcelCell(e);
  }

  public async doExportExcel() {
    this.ui.operationBegin("Excel dannes");
    const workbook = await this.doExportExcelRun();
    this.ui.operationEnd(`Excel med ${this.excelWorkbookRowsGet(workbook?.worksheets)} rækker dannet`);
  }

  public excelWorkbookRowsGet(worksheets: any): number {
    var rows = 0;
    worksheets?.forEach((s: any) => rows += s?.rows?.length ?? 0);
    return rows;
  }

  public doExportExcelPageSetup(workbook: any) {
    this.ui.operationProgress(`${this.excelWorkbookRowsGet(workbook?.worksheets)} rækker dannet`);
  }

  public async doExportExcelRun() {
    var p: ExcelExportProperties = {
      enableFilter: true,
      theme:
      {
        header: { fontName: 'Aptos Narrow', backColor: '#E1E1EE', fontColor: '#000000' },
        record: { fontName: 'Aptos Narrow', fontColor: '#000000' },
        caption: { fontName: 'Aptos Narrow', backColor: '#AAAAAA', fontColor: '#000000', bold: false }
      }

    };

    // Columns (visible+order)
    p.columns = [];
    const fields: FieldMetadata[] = this.data.fieldMetadata?.filter(f => f.visibleExport == true || f.orderExport != null);
    if (fields?.length > 0) {
      fields.filter(f => f.visibleExport == true)
        .sort(function (a, b) { return (a.orderExport - b.orderExport) })
        .forEach(f => {
          var obj: any = Object.assign({}, this.grid.getColumnByField(f.name));
          if (f.widthExport != null) {
            obj.width = f.widthExport;
          }
          p.columns.push(obj);
        });
    } else {
      const visibleCols = this.grid.getVisibleColumns().map(c => Object.assign({}, c));
      const pkField = this.data.viewMetadata.primaryKey as string;
      this.addColumn(pkField, visibleCols, true);
      const pkFieldDb = this.data.viewMetadata.primaryKeyDb as string;
      this.addColumn(pkFieldDb, visibleCols, true);
      p.columns = visibleCols;
    }
    this.tags = p.columns.map(c => `${c.field}="${c.headerText}"`).join(", ");

    const meta: ViewMetadata = this.data?.viewMetadata;
    if (meta != null) {

      var fileName = '';

      if (meta.exportFileName?.length > 0) {
        fileName = meta.exportFileName;
        if (fileName.length > 0) {
          p.header = {
            rows: [{
              index: 1,
              cells: [{
                index: 1,
                value: fileName,
                style: { fontColor: '#000000', bold: true, fontSize: 16 },
              }]
            },
            {
              index: 2,
              cells: []
            }]
          };
        }
        const lettersToRemove = '[]*/\?:';
        for (const letter of lettersToRemove) {
          fileName = fileName.replaceAll(letter, "");
        }
      }

      if (meta.name) {
        if (fileName == null || fileName.length == 0) {
          fileName = (meta.name ?? meta.dataApiName) + '-' + this.getDateStampStr();
        }
      }
      p.fileName = fileName + '.xlsx';

      if (meta.offline != null && meta.offline === false) {
        var ds: any = this.grid?.renderModule?.data?.dataManager?.dataSource;
        if (ds && ds.offline == true) {
          ds.offline = false;
        }
      }
    }

    /*
    p.multipleExport = { type: 'NewSheet' };
    p.hierarchyExportMode = "All";
    this.grid.excelExport(p, true).then((workbook: any) => {
      var len = workbook.worksheets[0].columns.length;
      for (var j = 0; j < len; j++) {
        workbook.worksheets[0].columns[j].width = 200;
      }
      workbook.save(fileName);
    });
    */

    var exportM: any = this.grid.excelExportModule;
    var renderM: any = this.grid.renderModule;
    if (exportM.data && renderM.data) {
      exportM.data = renderM.data;
    }

    var helper: any = (<any>this.grid.excelExportModule).helper;
    if (helper && (<any>ExportHelper.prototype).getFData_Orig == undefined) {
      (<any>ExportHelper.prototype).getFData_Orig = ExportHelper.prototype.getFData.bind(helper);
      (<any>ExportHelper.prototype).getFData_Array = CerGridExcelService.ExportHelper_GetFData_Array.bind(helper);
      ExportHelper.prototype.getFData = CerGridExcelService.ExportHelper_GetFData_Custom.bind(helper);
    }

    return await this.grid.excelExport(p);
  }

  private addColumn(field: string, visibleCols: Column[], hidden: boolean) {
    if (field?.length > 0) {
      var listCol = visibleCols.find(c => c.field == field);
      if (listCol == null) {
        listCol = this.grid.getColumnByField(field);
        if (listCol != null) {
          const excelCol = Object.assign({}, listCol);
          if (hidden) {
            excelCol.width = '0px';
          }
          excelCol.visible = true;
          visibleCols.push(excelCol);
        }
      }
    }
  }

  public static ExportHelper_GetFData_Array(value: any, column: any): any[] {
    var fkValues: string[] = [];
    (<any[]>value).forEach(v => {
      const fkRow = v?.length > 0 ? getForeignData(column, {}, parseInt(v), (<any>this).foreignKeyData[column.field])[0] : null;
      if (fkRow != null) {
        fkValues.push(getValue(column.foreignKeyValue, fkRow));
      }
    });
    var fkData: any = {}
    fkData[column.foreignKeyValue] = fkValues.join(', ');
    return fkData;
  }

  public static ExportHelper_GetFData_Custom(value: any, column: any): any {
    if (value != null && Array.isArray(value)) {
      return (<any>this).getFData_Array(value, column);
    }
    if (!CerGridFilterService.isForeignColumnReplacement(column)) {
      var res: any = {};
      setDeepValue(res, value, column.foreignKeyValue ?? column.field);
      return res;
    }
    return (<any>this).getFData_Orig(value, column);
  }

  private getDateStampStr() {
    return this.datePipe.transform(new Date(), 'yyyy-MM-dd-hh-mm');
  }

}

export function setDeepValue(obj: any, value: any, path: string | string[]) {
  if (typeof path === "string") {
    const paths = path.split('.');
    setDeepValueArray(obj, value, paths);
  }
  if (Array.isArray(path)) {
    setDeepValueArray(obj, value, path);
  }
}

export function setDeepValueArray(obj: any, value: any, paths: string[]) {
  if (paths.length > 1) {
    var p = paths.shift();
    if (obj[p] == null || typeof obj[p] !== 'object') {
      obj[p] = {};
    }
    setDeepValueArray(obj[p], value, paths);
  } else {
    obj[paths[0]] = value;
  }
}
